<template>
  <div>
    <AppHeader/>
    <!-- 主视觉区域 - 联系我们 -->
    <section class="relative h-96 flex items-center justify-center text-white bg-gradient-stone pt-16 overflow-hidden">
      <div class="absolute inset-0">
        <img :alt="$t('contact.hero.title')" class="w-full h-full object-cover opacity-40"
          src="/images/coreService/service-hero-bg.jpg">
      </div>
      <div class="relative z-10 text-center px-4 max-w-4xl">
        <h1 class="text-5xl md:text-6xl font-extrabold leading-tight mb-4 animate-fade-in-up">
          {{ $t('contact.hero.title') }}
        </h1>
        <p class="text-xl md:text-2xl mb-6 max-w-3xl mx-auto animate-fade-in-up delay-200">
          {{ $t('contact.hero.subtitle') }}
        </p>
      </div>
    </section>

    <!-- 咨询表单与商务合作 -->
    <section class="py-20 px-6 md:px-12 bg-gray-50">
      <div class="max-w-7xl mx-auto">
        <!-- 标签切换 -->
        <div class="flex justify-center mb-12">
          <div class="bg-white rounded-lg shadow-md p-2 inline-flex">
            <button
              @click="activeTab = 'consultation'"
              :class="[
                'px-8 py-3 rounded-lg font-semibold transition duration-300',
                activeTab === 'consultation'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              <i class="fas fa-paper-plane mr-2"></i>
              {{ $t('contact.formSection.consultation.tab') }}
            </button>
            <button
              @click="activeTab = 'partnership'"
              :class="[
                'px-8 py-3 rounded-lg font-semibold transition duration-300',
                activeTab === 'partnership'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              ]"
            >
              <i class="fas fa-handshake mr-2"></i>
              {{ $t('contact.formSection.partnership.tab') }}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- 左侧 - 说明信息 -->
          <div class="bg-white rounded-xl shadow-xl p-8">
            <div v-if="activeTab === 'consultation'">
              <h2 class="text-3xl font-extrabold mb-6 text-gray-900">
                <i class="fas fa-paper-plane text-purple-600 mr-3"></i>
                {{ $t('contact.formSection.consultation.title') }}
              </h2>
              <p class="text-gray-600 mb-6 leading-relaxed">
                {{ $t('contact.formSection.consultation.description') }}
              </p>
              <div class="space-y-4">
                <div class="flex items-center text-gray-700">
                  <i class="fas fa-check-circle text-green-500 mr-3"></i>
                  <span>{{ $t('contact.formSection.consultation.benefits.analysis') }}</span>
                </div>
                <div class="flex items-center text-gray-700">
                  <i class="fas fa-check-circle text-green-500 mr-3"></i>
                  <span>{{ $t('contact.formSection.consultation.benefits.demo') }}</span>
                </div>
                <div class="flex items-center text-gray-700">
                  <i class="fas fa-check-circle text-green-500 mr-3"></i>
                  <span>{{ $t('contact.formSection.consultation.benefits.response') }}</span>
                </div>
                <div class="flex items-center text-gray-700">
                  <i class="fas fa-check-circle text-green-500 mr-3"></i>
                  <span>{{ $t('contact.formSection.consultation.benefits.privacy') }}</span>
                </div>
              </div>
            </div>
            <div v-else>
              <h2 class="text-3xl font-extrabold mb-6 text-gray-900">
                <i class="fas fa-handshake text-purple-600 mr-3"></i>
                {{ $t('contact.formSection.partnership.title') }}
              </h2>
              <p class="text-gray-600 mb-6 leading-relaxed">
                {{ $t('contact.formSection.partnership.description') }}
              </p>
              <div class="space-y-6">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">
                    <i class="fas fa-network-wired text-purple-600 mr-2"></i>
                    {{ $t('contact.formSection.partnership.benefits.channel') }}
                  </h3>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">
                    <i class="fas fa-code-branch text-purple-600 mr-2"></i>
                    {{ $t('contact.formSection.partnership.benefits.tech') }}
                  </h3>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">
                    <i class="fas fa-puzzle-piece text-purple-600 mr-2"></i>
                    {{ $t('contact.formSection.partnership.benefits.solution') }}
                  </h3>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">
                    <i class="fas fa-users text-purple-600 mr-2"></i>
                    {{ $t('contact.formSection.partnership.benefits.support') }}
                  </h3>
                </div>
              </div>
            </div>
          </div>

        <!-- 右侧 - 联系表单 -->
        <div class="bg-white rounded-xl shadow-xl p-6">
          <h2 class="text-2xl font-extrabold mb-4 text-gray-900">
            {{ $t('contact.form.title') }}
          </h2>
          
          <form @submit.prevent="submitForm" class="space-y-3">
            <!-- 两列布局 -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
              <!-- 咨询类型/合作类型 -->
              <div>
                <label for="inquiryType" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ activeTab === 'consultation' ? $t('contact.formSection.inquiryType.label') : $t('contact.formSection.inquiryType.partnershipLabel') }} *
                </label>
                <select
                  id="inquiryType"
                  v-model="formData.inquiryType"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                >
                  <option value="">{{ $t('contact.formSection.inquiryType.placeholder') }}</option>
                  <template v-if="activeTab === 'consultation'">
                    <option value="solution">{{ $t('contact.formSection.inquiryType.consultation.solution') }}</option>
                    <option value="product">{{ $t('contact.formSection.inquiryType.consultation.product') }}</option>
                    <option value="pricing">{{ $t('contact.formSection.inquiryType.consultation.pricing') }}</option>
                    <option value="demo">{{ $t('contact.formSection.inquiryType.consultation.demo') }}</option>
                    <option value="support">{{ $t('contact.formSection.inquiryType.consultation.support') }}</option>
                  </template>
                  <template v-else>
                    <option value="channel">{{ $t('contact.formSection.inquiryType.partnership.channel') }}</option>
                    <option value="technology">{{ $t('contact.formSection.inquiryType.partnership.technology') }}</option>
                    <option value="integration">{{ $t('contact.formSection.inquiryType.partnership.integration') }}</option>
                    <option value="consulting">{{ $t('contact.formSection.inquiryType.partnership.consulting') }}</option>
                    <option value="other">{{ $t('contact.formSection.inquiryType.partnership.other') }}</option>
                  </template>
                </select>
                <span v-if="errors.inquiryType" class="text-red-500 text-xs mt-0.5 block">{{ errors.inquiryType }}</span>
              </div>

              <!-- 服务地区 -->
              <div>
                <label for="region" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.formSection.region.label') }} *
                </label>
                <select
                  id="region"
                  v-model="formData.region"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                >
                  <option value="">{{ $t('contact.formSection.region.placeholder') }}</option>
                  <option value="china">{{ $t('contact.formSection.region.china') }}</option>
                  <option value="hongkong">{{ $t('contact.formSection.region.hongkong') }}</option>
                  <option value="taiwan">{{ $t('contact.formSection.region.taiwan') }}</option>
                  <option value="singapore">{{ $t('contact.formSection.region.singapore') }}</option>
                  <option value="japan">{{ $t('contact.formSection.region.japan') }}</option>
                  <option value="korea">{{ $t('contact.formSection.region.korea') }}</option>
                </select>
                <span v-if="errors.region" class="text-red-500 text-xs mt-0.5 block">{{ errors.region }}</span>
              </div>

              <!-- 公司名称 -->
              <div>
                <label for="companyName" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.companyName') }} *
                </label>
                <input
                  id="companyName"
                  v-model="formData.companyName"
                  type="text"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                  :placeholder="$t('contact.form.companyNamePlaceholder')"
                >
                <span v-if="errors.companyName" class="text-red-500 text-xs mt-0.5 block">{{ errors.companyName }}</span>
              </div>

              <!-- 联系人姓名 -->
              <div>
                <label for="contactName" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.contactName') }} *
                </label>
                <input
                  id="contactName"
                  v-model="formData.contactName"
                  type="text"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                  :placeholder="$t('contact.form.contactNamePlaceholder')"
                >
                <span v-if="errors.contactName" class="text-red-500 text-xs mt-0.5 block">{{ errors.contactName }}</span>
              </div>

              <!-- 职位 -->
              <div>
                <label for="position" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.position') }} *
                </label>
                <input
                  id="position"
                  v-model="formData.position"
                  type="text"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                  :placeholder="$t('contact.form.positionPlaceholder')"
                >
                <span v-if="errors.position" class="text-red-500 text-xs mt-0.5 block">{{ errors.position }}</span>
              </div>

              <!-- 邮箱 -->
              <div>
                <label for="email" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.email') }} *
                </label>
                <input
                  id="email"
                  v-model="formData.email"
                  type="email"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                  :placeholder="$t('contact.form.emailPlaceholder')"
                >
                <span v-if="errors.email" class="text-red-500 text-xs mt-0.5 block">{{ errors.email }}</span>
              </div>

              <!-- 电话 -->
              <div>
                <label for="phone" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.phone') }} *
                </label>
                <input
                  id="phone"
                  v-model="formData.phone"
                  type="tel"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                  :placeholder="$t('contact.form.phonePlaceholder')"
                >
                <span v-if="errors.phone" class="text-red-500 text-xs mt-0.5 block">{{ errors.phone }}</span>
              </div>

              <!-- 行业 -->
              <div>
                <label for="industry" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.industry') }} *
                </label>
                <select
                  id="industry"
                  v-model="formData.industry"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                >
                  <option value="">{{ $t('contact.form.industryPlaceholder') }}</option>
                  <option value="technology">{{ $t('contact.form.industries.technology') }}</option>
                  <option value="finance">{{ $t('contact.form.industries.finance') }}</option>
                  <option value="healthcare">{{ $t('contact.form.industries.healthcare') }}</option>
                  <option value="education">{{ $t('contact.form.industries.education') }}</option>
                  <option value="manufacturing">{{ $t('contact.form.industries.manufacturing') }}</option>
                  <option value="retail">{{ $t('contact.form.industries.retail') }}</option>
                  <option value="other">{{ $t('contact.form.industries.other') }}</option>
                </select>
                <span v-if="errors.industry" class="text-red-500 text-xs mt-0.5 block">{{ errors.industry }}</span>
              </div>

              <!-- 公司规模 -->
              <div>
                <label for="companySize" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.companySize') }} *
                </label>
                <select
                  id="companySize"
                  v-model="formData.companySize"
                  required
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                >
                  <option value="">{{ $t('contact.form.companySizePlaceholder') }}</option>
                  <option value="1-10">1-10 {{ $t('contact.form.employees') }}</option>
                  <option value="11-50">11-50 {{ $t('contact.form.employees') }}</option>
                  <option value="51-200">51-200 {{ $t('contact.form.employees') }}</option>
                  <option value="201-1000">201-1000 {{ $t('contact.form.employees') }}</option>
                  <option value="1000+">1000+ {{ $t('contact.form.employees') }}</option>
                </select>
                <span v-if="errors.companySize" class="text-red-500 text-xs mt-0.5 block">{{ errors.companySize }}</span>
              </div>

              <!-- 项目预算 -->
              <div>
                <label for="budget" class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('contact.form.budget') }}
                </label>
                <select
                  id="budget"
                  v-model="formData.budget"
                  class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                >
                  <option value="">{{ $t('contact.form.budgetPlaceholder') }}</option>
                  <option value="under-50k">{{ $t('contact.form.budgetOptions.under50k') }}</option>
                  <option value="50k-100k">{{ $t('contact.form.budgetOptions.range50k100k') }}</option>
                  <option value="100k-500k">{{ $t('contact.form.budgetOptions.range100k500k') }}</option>
                  <option value="500k-1m">{{ $t('contact.form.budgetOptions.range500k1m') }}</option>
                  <option value="over-1m">{{ $t('contact.form.budgetOptions.over1m') }}</option>
                </select>
              </div>
            </div>

            <!-- 感兴趣的服务 (单独一行) -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">
                {{ $t('contact.form.interestedServices') }} *
              </label>
              <div class="grid grid-cols-2 gap-2">
                <label class="flex items-center">
                  <input
                    type="checkbox"
                    v-model="formData.services"
                    value="ticket-system"
                    class="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5"
                  >
                  <span class="ml-1.5 text-xs text-gray-700">{{ $t('contact.form.serviceOptions.ticketSystem') }}</span>
                </label>
                <label class="flex items-center">
                  <input
                    type="checkbox"
                    v-model="formData.services"
                    value="ai-solutions"
                    class="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5"
                  >
                  <span class="ml-1.5 text-xs text-gray-700">{{ $t('contact.form.serviceOptions.aiSolutions') }}</span>
                </label>
                <label class="flex items-center">
                  <input
                    type="checkbox"
                    v-model="formData.services"
                    value="data-analytics"
                    class="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5"
                  >
                  <span class="ml-1.5 text-xs text-gray-700">{{ $t('contact.form.serviceOptions.dataAnalytics') }}</span>
                </label>
                <label class="flex items-center">
                  <input
                    type="checkbox"
                    v-model="formData.services"
                    value="consulting"
                    class="rounded border-gray-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5"
                  >
                  <span class="ml-1.5 text-xs text-gray-700">{{ $t('contact.form.serviceOptions.consulting') }}</span>
                </label>
              </div>
              <span v-if="errors.services" class="text-red-500 text-xs mt-0.5 block">{{ errors.services }}</span>
            </div>

            <!-- 项目描述 (单独一行) -->
            <div>
              <label for="message" class="block text-xs font-medium text-gray-700 mb-1">
                {{ $t('contact.form.message') }} *
              </label>
              <textarea
                id="message"
                v-model="formData.message"
                rows="3"
                required
                class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition duration-300"
                :placeholder="$t('contact.form.messagePlaceholder')"
              ></textarea>
              <span v-if="errors.message" class="text-red-500 text-xs mt-0.5 block">{{ errors.message }}</span>
            </div>

            <!-- 提交按钮 -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="isSubmitting"
                class="w-full bg-purple-600 text-white py-2.5 px-6 rounded-lg font-semibold text-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span v-if="!isSubmitting">
                  <i class="fas fa-paper-plane mr-2"></i>
                  {{ $t('contact.form.submit') }}
                </span>
                <span v-else>
                  <i class="fas fa-spinner animate-spin mr-2"></i>
                  {{ $t('contact.form.submitting') }}
                </span>
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
    </section>

    <!-- 即时沟通 -->
    <section class="py-16 px-6 md:px-12 bg-gradient-to-br from-purple-50 to-blue-50">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-12">
          <h2 class="text-4xl font-extrabold text-gray-900 mb-4">{{ $t('contact.instantCommunication.title') }}</h2>
          <p class="text-xl text-gray-600">{{ $t('contact.instantCommunication.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 gap-8 max-w-2xl mx-auto">
          <!-- 在线客服模块按需求注释隐藏
          <div class="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300">
            <div class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fas fa-comments text-blue-600 text-3xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ $t('contact.instantCommunication.liveChat.title') }}</h3>
            <p class="text-gray-600 mb-6">{{ $t('contact.instantCommunication.liveChat.description') }}</p>
            <button
                @click="openLiveChat"
                class="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300"
            >
              <i class="fas fa-comment-dots mr-2"></i>
              {{ $t('contact.instantCommunication.liveChat.button') }}
            </button>
            <p class="text-sm text-gray-500 mt-4">{{ $t('contact.instantCommunication.liveChat.hours') }}</p>
          </div>
          -->

          <!-- 企业微信 -->
          <div class="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition duration-300">
            <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fab fa-weixin text-green-600 text-3xl"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ $t('contact.instantCommunication.wechat.title') }}</h3>
            <p class="text-gray-600 mb-6">{{ $t('contact.instantCommunication.wechat.description') }}</p>
            <figure class="mx-auto mb-4 w-40 max-w-full">
              <div class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <img
                  src="/images/contact/wechat_scan.png"
                  width="160"
                  height="160"
                  class="block h-40 w-40 max-w-full object-contain"
                  :alt="$t('contact.instantCommunication.wechat.qrcodeAlt')"
                  loading="lazy"
                  decoding="async"
                >
              </div>
            </figure>
            <p class="text-sm text-gray-500">{{ $t('contact.instantCommunication.wechat.qrcode') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 成功提示模态框 -->
    <div v-if="showSuccessModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-8 max-w-md mx-4 text-center">
        <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i class="fas fa-check text-green-600 text-2xl"></i>
        </div>
        <h3 class="text-2xl font-bold text-gray-900 mb-2">{{ $t('contact.success.title') }}</h3>
        <p class="text-gray-600 mb-6">{{ $t('contact.success.message') }}</p>
        <button
          @click="closeSuccessModal"
          class="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition duration-300"
        >
          {{ $t('contact.success.close') }}
        </button>
      </div>
    </div>

    <!-- 底部页脚 -->
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { addWebsite } from '@/api/website.js'
import { isValidEmail, isValidPhone, generateSessionId, generateDeviceFingerprint, generateSubmissionHash } from '@/utils/index.js'
import { getStorage, setStorage } from '@/utils/storage.js'

const { t } = useI18n()

// 活动标签（业务咨询/商务合作）
const activeTab = ref('consultation')
// 表单数据
const formData = reactive({
  inquiryType: '',
  region: '',
  companyName: '',
  contactName: '',
  position: '',
  email: '',
  phone: '',
  industry: '',
  companySize: '',
  services: [],
  budget: '',
  message: ''
})

// 表单验证错误
const errors = reactive({})

// 状态
const isSubmitting = ref(false)
const showSuccessModal = ref(false)

// 表单验证
const validateForm = () => {
  // 清空之前的错误
  Object.keys(errors).forEach(key => delete errors[key])

  // 验证咨询/合作类型
  if (!formData.inquiryType) {
    errors.inquiryType = t('common.invalidType')
  }

  // 验证服务地区
  if (!formData.region) {
    errors.region = t('common.invalidRegion')
  }

  // 验证必填字段
  if (!formData.companyName.trim()) {
    errors.companyName = t('common.invalidCompanyName')
  }
  
  if (!formData.contactName.trim()) {
    errors.contactName = t('common.invalidContactName')
  }
  
  if (!formData.position.trim()) {
    errors.position = t('common.invalidPosition')
  }
  
  if (!formData.email.trim()) {
    errors.email = t('common.invalidEmail')
  } else if (!isValidEmail(formData.email)) {
    errors.email = t('common.invalidEmailFormat')
  }
  
  if (!formData.phone.trim()) {
    errors.phone = t('common.invalidPhone')
  } else if (!isValidPhone(formData.phone)) {
    errors.phone = t('common.invalidPhoneFormat')
  }
  
  if (!formData.industry) {
    errors.industry = t('common.invalidIndustry')
  }
  
  if (!formData.companySize) {
    errors.companySize = t('common.invalidCompanySize')
  }
  
  if (formData.services.length === 0) {
    errors.services = t('common.invalidServices')
  }
  
  if (!formData.message.trim()) {
    errors.message = t('common.invalidMessage')
  }

  return Object.keys(errors).length === 0
}

// 生成或获取会话相关信息
const getSessionInfo = () => {
  // 获取或生成会话ID
  let sessionId = getStorage('contact_session_id')
  if (!sessionId) {
    sessionId = generateSessionId()
    setStorage('contact_session_id', sessionId, 24 * 60 * 60 * 1000) // 24小时过期
  }

  // 获取或生成Cookie ID
  let cookieId = getStorage('contact_cookie_id')
  if (!cookieId) {
    cookieId = `cookie_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setStorage('contact_cookie_id', cookieId, 30 * 24 * 60 * 60 * 1000) // 30天过期
  }

  // 获取或生成设备ID
  let deviceId = getStorage('contact_device_id')
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setStorage('contact_device_id', deviceId, 365 * 24 * 60 * 60 * 1000) // 1年过期
  }

  return {
    sessionId,
    cookieId,
    deviceId
  }
}

// 生成表单令牌
const generateFormToken = () => {
  return `csrf_token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

const trimValue = (value) => {
  return typeof value === 'string' ? value.trim() : value
}

const getRegionLabel = (region) => {
  const regionMap = {
    china: 'contact.formSection.region.china',
    hongkong: 'contact.formSection.region.hongkong',
    taiwan: 'contact.formSection.region.taiwan',
    singapore: 'contact.formSection.region.singapore',
    japan: 'contact.formSection.region.japan',
    korea: 'contact.formSection.region.korea'
  }

  return regionMap[region] ? t(regionMap[region]) : region
}

const getInquiryTypeLabel = (inquiryType) => {
  const consultationMap = {
    solution: 'contact.formSection.inquiryType.consultation.solution',
    product: 'contact.formSection.inquiryType.consultation.product',
    pricing: 'contact.formSection.inquiryType.consultation.pricing',
    demo: 'contact.formSection.inquiryType.consultation.demo',
    support: 'contact.formSection.inquiryType.consultation.support'
  }
  const partnershipMap = {
    channel: 'contact.formSection.inquiryType.partnership.channel',
    technology: 'contact.formSection.inquiryType.partnership.technology',
    integration: 'contact.formSection.inquiryType.partnership.integration',
    consulting: 'contact.formSection.inquiryType.partnership.consulting',
    other: 'contact.formSection.inquiryType.partnership.other'
  }
  const typeMap = activeTab.value === 'consultation' ? consultationMap : partnershipMap

  return typeMap[inquiryType] ? t(typeMap[inquiryType]) : inquiryType
}

const getServiceLabel = (service) => {
  const serviceMap = {
    'ticket-system': 'contact.form.serviceOptions.ticketSystem',
    'ai-solutions': 'contact.form.serviceOptions.aiSolutions',
    'data-analytics': 'contact.form.serviceOptions.dataAnalytics',
    consulting: 'contact.form.serviceOptions.consulting'
  }

  return serviceMap[service] ? t(serviceMap[service]) : service
}

const getIndustryLabel = (industry) => {
  const industryMap = {
    technology: 'contact.form.industries.technology',
    finance: 'contact.form.industries.finance',
    healthcare: 'contact.form.industries.healthcare',
    education: 'contact.form.industries.education',
    manufacturing: 'contact.form.industries.manufacturing',
    retail: 'contact.form.industries.retail',
    other: 'contact.form.industries.other'
  }

  return industryMap[industry] ? t(industryMap[industry]) : industry
}

const getCompanySizeLabel = (companySize) => {
  const sizeMap = {
    '1-10': `1-10 ${t('contact.form.employees')}`,
    '11-50': `11-50 ${t('contact.form.employees')}`,
    '51-200': `51-200 ${t('contact.form.employees')}`,
    '201-1000': `201-1000 ${t('contact.form.employees')}`,
    '1000+': `1000+ ${t('contact.form.employees')}`
  }

  return sizeMap[companySize] || companySize
}

const getBudgetLabel = (budget) => {
  const budgetMap = {
    'under-50k': 'contact.form.budgetOptions.under50k',
    '50k-100k': 'contact.form.budgetOptions.range50k100k',
    '100k-500k': 'contact.form.budgetOptions.range100k500k',
    '500k-1m': 'contact.form.budgetOptions.range500k1m',
    'over-1m': 'contact.form.budgetOptions.over1m'
  }

  return budgetMap[budget] ? t(budgetMap[budget]) : budget
}

const buildProjectDescription = () => {
  const inquiryTypeKey = formData.inquiryType || 'inquiryType'
  const selectedInfo = {
    formType: activeTab.value,
    formTypeLabel: activeTab.value === 'consultation'
      ? t('contact.formSection.consultation.tab')
      : t('contact.formSection.partnership.tab'),
    inquiryType: formData.inquiryType,
    inquiryTypeLabel: getInquiryTypeLabel(formData.inquiryType),
    region: formData.region,
    regionLabel: getRegionLabel(formData.region),
    industry: formData.industry,
    industryLabel: getIndustryLabel(formData.industry),
    companySize: formData.companySize,
    companySizeLabel: getCompanySizeLabel(formData.companySize),
    budgetRange: formData.budget || null,
    budgetRangeLabel: formData.budget ? getBudgetLabel(formData.budget) : null,
    services: formData.services,
    serviceLabels: formData.services.map(getServiceLabel)
  }

  return JSON.stringify({
    [inquiryTypeKey]: selectedInfo,
    projectDesc: trimValue(formData.message)
  })
}

const buildWebsitePayload = () => {
  const { sessionId, cookieId, deviceId } = getSessionInfo()
  const clientId = `client_web_${Date.now()}`
  const formToken = generateFormToken()
  const userFingerprint = generateDeviceFingerprint()
  const source = activeTab.value === 'consultation' ? 'website_consultation_form' : 'website_partnership_form'

  return {
    companyName: trimValue(formData.companyName),
    contactName: trimValue(formData.contactName),
    position: trimValue(formData.position),
    email: trimValue(formData.email),
    phone: trimValue(formData.phone),
    industry: formData.industry,
    companySize: formData.companySize,
    interestedServices: JSON.stringify(formData.services),
    budgetRange: formData.budget || null,
    projectDescription: buildProjectDescription(),
    status: 'new',
    priority: 'medium',
    sessionId,
    cookieId,
    userFingerprint,
    formToken,
    submissionHash: generateSubmissionHash({
      email: trimValue(formData.email),
      phone: trimValue(formData.phone),
      companyName: trimValue(formData.companyName),
      timestamp: Date.now()
    }),
    deviceId,
    clientId,
    source,
    userAgent: navigator.userAgent,
    customData: JSON.stringify({
      form_version: '2.0',
      form_type: activeTab.value,
      form_type_label: activeTab.value === 'consultation'
        ? t('contact.formSection.consultation.tab')
        : t('contact.formSection.partnership.tab'),
      inquiry_type: formData.inquiryType,
      inquiry_type_label: getInquiryTypeLabel(formData.inquiryType),
      region: formData.region,
      region_label: getRegionLabel(formData.region),
      services: formData.services,
      service_labels: formData.services.map(getServiceLabel),
      page_url: window.location.href,
      referrer: document.referrer || '',
      screen_resolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      submit_timestamp: new Date().toISOString()
    })
  }
}

// 提交表单
const submitForm = async () => {
  if (!validateForm() || isSubmitting.value) {
    return
  }

  isSubmitting.value = true

  try {
    const submitData = buildWebsitePayload()
    
    // 调用API提交表单数据
    await addWebsite(submitData)
    
    // 显示成功提示
    showSuccessModal.value = true
    
    // 重置表单
    Object.keys(formData).forEach(key => {
      if (Array.isArray(formData[key])) {
        formData[key] = []
      } else {
        formData[key] = ''
      }
    })
    
    // 清除表单相关的会话信息（可选）
    // setStorage('contact_session_id', null)
    
  } catch (error) {
    console.error('提交表单失败:', error)
    
    // 根据错误类型显示不同的提示信息
    let errorMessage = t('common.submitFailedRetry')
    
    if (error.response) {
      // 服务器返回错误响应
      if (error.response.status === 400) {
        errorMessage = t('common.submitFailedCheck')
      } else if (error.response.status === 500) {
        errorMessage = t('common.submitFailedServer')
      } else if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message
      }
    } else if (error.request) {
      // 网络错误
      errorMessage = t('common.submitFailedNetwork')
    }
    
    alert(errorMessage)
  } finally {
    isSubmitting.value = false
  }
}

// 关闭成功提示模态框
const closeSuccessModal = () => {
  showSuccessModal.value = false
}
</script>
